import { useState } from 'react';
import { Form, Input, Modal, Radio, Tag, notification } from 'antd';
import CustomButton from 'components/custom-button';
import type { FlagRow, FlagSeverity } from '../contract';
import { closeFlag } from '../api';
import { mapStudioError } from '../studio-error';
import {
  FLAG_SEVERITY_COLOR,
  FLAG_SEVERITY_LABEL,
  FLAG_STATUS_LABEL,
  REASON_MAX,
  REASON_MIN,
  flagLabel,
} from '../labels';

export interface CloseFlagTarget {
  id: number;
  code?: string;
  label?: string | null;
  severity?: FlagSeverity;
  detail?: string | null;
}

export interface CloseFlagModalProps {
  /** Cờ cần khép; null = đóng modal. */
  flag: CloseFlagTarget | null;
  onClose: () => void;
  onDone?: (row: FlagRow) => void;
}

interface FormValues {
  status: 'OK' | 'ACTION';
  note: string;
}

/**
 * Admin khép một cờ (mục 8.7.9, 8.7.10): PATCH /approvals/flags/:id { status, note }. Hợp lệ (OK) hoặc Cần
 * xử lý (ACTION), ghi chú 5–500 ký tự. Máy chủ chỉ cho Admin (FLAG_CLOSE).
 */
const CloseFlagModal = ({ flag, onClose, onDone }: CloseFlagModalProps) => {
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);

  const handleClose = () => {
    if (saving) return;
    form.resetFields();
    onClose();
  };

  const onFinish = async (values: FormValues) => {
    if (!flag) return;
    setSaving(true);
    try {
      const row = await closeFlag(flag.id, { status: values.status, note: values.note.trim() });
      notification.success({ message: 'Đã khép cờ.' });
      form.resetFields();
      onDone?.(row);
      onClose();
    } catch (err) {
      notification.error({ message: mapStudioError(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Khép cờ"
      open={!!flag}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
      maskClosable={false}
      width={520}
    >
      {flag && (
        <div style={{ marginBottom: 16 }}>
          {flag.severity && (
            <Tag color={FLAG_SEVERITY_COLOR[flag.severity]}>{FLAG_SEVERITY_LABEL[flag.severity]}</Tag>
          )}
          <strong>{flagLabel(flag.code || '', flag.label)}</strong>
          {flag.detail && <div style={{ marginTop: 6 }}>{flag.detail}</div>}
        </div>
      )}
      <Form
        form={form}
        layout="vertical"
        className="custom-form"
        initialValues={{ status: 'OK' }}
        onFinish={onFinish}
      >
        <Form.Item label="Kết luận" name="status" rules={[{ required: true }]}>
          <Radio.Group>
            <Radio value="OK">{FLAG_STATUS_LABEL.OK}</Radio>
            <Radio value="ACTION">{FLAG_STATUS_LABEL.ACTION}</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          label="Ghi chú"
          name="note"
          rules={[
            {
              validator: (_, value?: string) =>
                (value || '').trim().length >= REASON_MIN
                  ? Promise.resolve()
                  : Promise.reject(new Error(`Ghi chú cần ít nhất ${REASON_MIN} ký tự.`)),
            },
          ]}
        >
          <Input.TextArea rows={3} maxLength={REASON_MAX} showCount placeholder="Nhập nội dung" />
        </Form.Item>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <CustomButton text="Huỷ" bgColor="primary-grey" onClick={handleClose} />
          <CustomButton
            text="Khép cờ"
            bgColor="secondary-green"
            htmlType="submit"
            loading={saving}
          />
        </div>
      </Form>
    </Modal>
  );
};

export default CloseFlagModal;
